import { useEffect, useState } from 'react';
import { fetchCustomerDirectory } from '@/lib/api/customers';
import { rejectionDetail } from '@/lib/api/errors';
import { helpdeskApi, type Account, type HelpdeskConfig } from '@/lib/api/helpdesk';
import { autoMatch, toBindings, type CustomerLinks, type MatchCustomer } from '@/lib/helpdesk/matchAccounts';
import { CustomerAccountMapper } from './CustomerAccountMapper';

const LOOKUP_WARNING = 'Zoho account lookup failed and no accounts are known yet. Reconnect Zoho to load them. Existing links are preserved.';
const CACHED_WARNING = 'Zoho account lookup failed, so only accounts from previously collected tickets are offered. Reconnect Zoho to load the full list.';
const LOAD_FAILED = 'Unable to load customer links. Please try again.';
const SAVE_FAILED = 'Unable to save customer links. Please try again.';
const SAVED = 'Customer links saved. Sync tickets to collect newly linked accounts.';

type Message = { text: string; tone: 'info' | 'error' };

const TONE = {
  info: { role: 'status', className: 'text-sm text-gray-700 dark:text-gray-300' },
  error: { role: 'alert', className: 'text-sm text-red-600 dark:text-red-400' },
} as const;

/** Order-independent fingerprint of the bindings a set of links would save. */
function bindingsKey(links: CustomerLinks, customers: MatchCustomer[]): string {
  return JSON.stringify(toBindings(links, customers).sort((a, b) => a.customer_id.localeCompare(b.customer_id)));
}

function markSaved(links: CustomerLinks): CustomerLinks {
  return Object.fromEntries(Object.entries(links).map(([id, link]) => [id, { ...link, origin: 'saved' as const }]));
}

/** Customer accounts tab for an existing integration: load, auto-match empty rows, save. */
export function CustomerAccountsTab({ config }: { config: HelpdeskConfig }) {
  const [customers, setCustomers] = useState<MatchCustomer[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [links, setLinks] = useState<CustomerLinks>({});
  const [baseline, setBaseline] = useState<CustomerLinks>({});
  const [warning, setWarning] = useState('');
  const [message, setMessage] = useState<Message | null>(null);
  const [busy, setBusy] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [directory, bindings, known] = await Promise.all([
          fetchCustomerDirectory(),
          helpdeskApi.getCustomerAccounts(config.id),
          helpdeskApi.getKnownAccounts(config.id).catch(() => [] as Account[]),
        ]);
        let zoho: Account[] = [];
        let lookupWarning = '';
        try {
          zoho = await helpdeskApi.getAccounts(config.id);
        } catch {
          lookupWarning = known.length ? CACHED_WARNING : LOOKUP_WARNING;
        }
        if (!active) return;
        // The live directory is authoritative; cached accounts keep mapping possible without it.
        const offered = new Map(known.map(account => [account.id, account]));
        zoho.forEach(account => offered.set(account.id, account));
        const available = [...offered.values()].sort((a, b) => a.name.localeCompare(b.name));
        const saved: CustomerLinks = Object.fromEntries(bindings.map(b => [
          b.customer_id, { account_id: b.account_id, account_name: b.account_name, origin: 'saved' as const },
        ]));
        setCustomers(directory.customers);
        setAccounts(available);
        setBaseline(saved);
        setLinks(autoMatch(directory.customers, available, saved));
        setWarning(lookupWarning);
        setReady(true);
      } catch {
        if (active) setMessage({ text: LOAD_FAILED, tone: 'error' });
      } finally {
        if (active) setBusy(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [config.id]);

  const dirty = bindingsKey(links, customers) !== bindingsKey(baseline, customers);

  const save = async () => {
    setBusy(true);
    try {
      await helpdeskApi.saveCustomerAccounts(config.id, toBindings(links, customers));
      const promoted = markSaved(links);
      setLinks(promoted);
      setBaseline(promoted);
      setMessage({ text: SAVED, tone: 'info' });
    } catch (error) {
      setMessage({ text: rejectionDetail(error) ?? SAVE_FAILED, tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Customer accounts</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Each dashboard customer links to one Zoho account. Matching names are linked automatically; select an
        account for the rest. Tickets for linked accounts are collected by the existing sync schedule.
      </p>
      {message && <p role={TONE[message.tone].role} className={TONE[message.tone].className}>{message.text}</p>}
      {ready && customers.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Customers will appear after AIQ discovery completes.</p>
      )}
      {ready && customers.length > 0 && (
        <CustomerAccountMapper customers={customers} accounts={accounts} links={links} onChange={setLinks}
          disabled={busy} warning={warning || undefined} />
      )}
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy || !ready || !dirty || customers.length === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {busy ? (ready ? 'Saving…' : 'Loading…') : 'Save customer links'}
        </button>
        {dirty && !busy && <span className="text-sm text-amber-700 dark:text-amber-400">Unsaved changes</span>}
      </div>
    </div>
  );
}
